import { selector, selectorFamily } from 'recoil'
import { keyFactory } from 'src/utils/recoil'
import {
  AMBrand,
  AMLocation,
  AMMobilityLocationProvider,
  AMMobilityLocationProviderIdRequest,
  amMobilityDataProviderEnum,
} from 'src/common-services'
import { alphamapClient } from 'src/clients'
import { convertDateToString, getDateWithoutBloodyTZ } from 'src/utils/date-utils'
import { PickersShortcutsItem } from '@mui/x-date-pickers-pro'
import { subMonths, subYears } from 'date-fns'

import * as searchAtoms from 'src/atoms/search'
import * as tenantOverviewAtoms from 'src/atoms/mobility/tenant-overview'
import { CustomFootFallDateRange } from 'src/atoms/custom-polygon'

const key = keyFactory('mobility')

export type AMMobilityLocation = AMLocation
export type MobilityDateRange = { start: Date; end: Date }

export const validDateRange = selector<MobilityDateRange>({
  key: key('valid-date-range'),
  get: async () => {
    const { from: start, to: end } = await alphamapClient.mobility.getDateRange()
    return {
      start: getDateWithoutBloodyTZ(start),
      end: getDateWithoutBloodyTZ(end),
    }
  },
})

export const validTTADateRangeById = selectorFamily<MobilityDateRange, AMMobilityLocationProviderIdRequest>({
  key: key('valid-tta-date-range-by-id'),
  get:
    (request) =>
    async ({ get }) => {
      const response = await alphamapClient.mobility.getTTADateRange(request)
      if (!response) {
        return get(validDateRange)
      }

      const { from: start, to: end } = response
      return {
        start: getDateWithoutBloodyTZ(start),
        end: getDateWithoutBloodyTZ(end),
      }
    },
})

export const validDateRangeArray = selector<[Date, Date]>({
  key: key('valid-date-range-array'),
  get: ({ get }) => {
    const { start, end } = get(validDateRange)
    return [start, end]
  },
})

export const validDateRangeString = selector<CustomFootFallDateRange>({
  key: key('valid-date-range-string'),
  get: async ({ get }) => {
    const { start, end } = get(validDateRange)
    return {
      start: convertDateToString(start),
      end: convertDateToString(end),
    }
  },
})

export const validStartDateString = selector<string>({
  key: key('valid-start-date-string'),
  get: async ({ get }) => {
    const { start } = get(validDateRangeString)
    return start
  },
})

export const validEndDateString = selector<string>({
  key: key('valid-end-date-string'),
  get: async ({ get }) => {
    const { end } = get(validDateRangeString)
    return end
  },
})

export const validMonthsAgoDate = selectorFamily<string, number>({
  key: key('valid-months-ago-date'),
  get:
    (months) =>
    async ({ get }) => {
      const { end } = get(validDateRange)
      const date = subMonths(end, months)
      return convertDateToString(date)
    },
})

export const validProviderEndDate = selectorFamily<
  string | undefined,
  { provider: amMobilityDataProviderEnum; locationIds: string[] }
>({
  key: key('valid-provider-end-date'),
  get:
    ({ provider, locationIds }) =>
    () => {
      return alphamapClient.mobility.getValidProviderEndDate(provider, locationIds)
    },
})

export const validAdvanEndDate = selectorFamily<string | undefined, string[]>({
  key: key('valid-advan-end-date'),
  get: (locationIds) => () => {
    return alphamapClient.mobility.getValidAdvanEndDate(locationIds)
  },
})

export const validVisitsEndDate = selectorFamily<string | undefined, string[]>({
  key: key('valid-visits-end-date'),
  get: (locationIds) => () => {
    return alphamapClient.mobility.getValidVisitsEndDate(locationIds)
  },
})

export const validProviderEndDateForLocations = selectorFamily<
  string,
  { provider: amMobilityDataProviderEnum; compareLocationIds: string[] }
>({
  key: key('valid-provider-end-date-for-locations'),
  get:
    ({ provider, compareLocationIds }) =>
    async ({ get }) => {
      const locationId = get(alphamapLocationId)
      const moilityEndDate = get(validEndDateString)
      if (!locationId) {
        return moilityEndDate
      }

      const locationIds = [locationId, ...compareLocationIds]
      const _endDate = get(validProviderEndDate({ provider, locationIds }))
      if (!_endDate) {
        return moilityEndDate
      }
      return _endDate
    },
})

export const validAdvanEndDateForLocations = selectorFamily<string, string[]>({
  key: key('valid-advan-end-date-for-locations'),
  get:
    (compareLocationIds) =>
    async ({ get }) => {
      const locationId = get(alphamapLocationId)
      const moilityEndDate = get(validEndDateString)
      if (!locationId) {
        return moilityEndDate
      }

      const locationIds = [locationId, ...compareLocationIds]
      const _endDate = get(validAdvanEndDate(locationIds))
      if (!_endDate) {
        return moilityEndDate
      }
      return _endDate
    },
})

export const validVisitsEndDateForLocations = selectorFamily<string, string[]>({
  key: key('valid-visits-end-date-for-locations'),
  get:
    (compareLocationIds) =>
    async ({ get }) => {
      const locationId = get(alphamapLocationId)
      const moilityEndDate = get(validEndDateString)
      if (!locationId) {
        return moilityEndDate
      }

      const locationIds = [locationId, ...compareLocationIds]
      const _endDate = get(validVisitsEndDate(locationIds))
      if (!_endDate) {
        return moilityEndDate
      }
      return _endDate
    },
})

// Primary location used to power the mobility charts
export const alphamapLocation = selector<AMMobilityLocation | undefined>({
  key: key('alphamap-location'),
  get: ({ get }) => {
    // Check if we are in shopping center mobility mode and use the shopping center if so
    const shoppingCenterMobility = get(tenantOverviewAtoms.isShoppingCenterMobility)
    if (shoppingCenterMobility) {
      return get(tenantOverviewAtoms.shoppingCenter)
    }

    // Use the current location if we have one
    const alphamapLocation = get(searchAtoms.selectedLocation)
    if (!alphamapLocation) {
      return undefined
    }
    const address = `${alphamapLocation.street}, ${alphamapLocation.city}, ${alphamapLocation.state} ${alphamapLocation.zipCode}`

    return {
      ...alphamapLocation,
      address,
    }
  },
})

export const alphamapLocationCenter = selector<number[] | undefined>({
  key: key('alphamap-location-center'),
  get: ({ get }) => {
    const _alphamapLocation = get(alphamapLocation)

    if (!_alphamapLocation) {
      return undefined
    }
    return [_alphamapLocation.latLng.coordinates[1], _alphamapLocation.latLng.coordinates[0]]
  },
})

export const alphamapLocationId = selector<string | undefined>({
  key: key('alphamap-location-id'),
  get: ({ get }) => {
    const _alphamapLocation = get(alphamapLocation)

    if (!_alphamapLocation) {
      return undefined
    }
    return _alphamapLocation.id
  },
})

export const alphamapBrand = selector<AMBrand | undefined>({
  key: key('alphamap-brand'),
  get: ({ get }) => {
    const _alphamapLocation = get(alphamapLocation)

    if (!_alphamapLocation || !_alphamapLocation.brandId || !_alphamapLocation.brandName) {
      return undefined
    }

    const brand: AMBrand = {
      id: _alphamapLocation.brandId,
      name: _alphamapLocation.brandName,
    }
    return brand
  },
})

export type ProvidersEnabled = {
  [key in AMMobilityLocationProvider]?: boolean
}

export const MAX_COMPARE_LOCATIONS = 7

export const validDateRangeShortcuts = selectorFamily<
  PickersShortcutsItem<[Date, Date]>[],
  { startDate?: Date; endDate?: Date }
>({
  key: key('valid-date-range-shortcuts'),
  get:
    ({ endDate, startDate }) =>
    ({ get }) => {
      const { start: startValid, end: endValid } = get(validDateRange)
      const start = startDate ?? startValid
      const end = endDate ?? endValid

      return [
        {
          label: 'Year to Date',
          getValue: () => {
            const to = end
            const from = new Date(to.getFullYear(), 0, 1)
            return [from, to]
          },
        },
        {
          label: 'Last 3 Months',
          getValue: () => {
            const to = end
            const from = subMonths(to, 2)
            return [from, to]
          },
        },
        {
          label: 'Last 6 Months',
          getValue: () => {
            const to = end
            const from = subMonths(to, 5)
            return [from, to]
          },
        },
        {
          label: 'Last 12 Months',
          getValue: () => {
            const to = end
            const from = subMonths(to, 12)
            return [from, to]
          },
        },
        {
          label: 'Last 2 Years',
          getValue: () => {
            const to = end
            const from = subYears(to, 2)
            return [from, to]
          },
        },
        {
          label: 'Last 3 Years',
          getValue: () => {
            const to = end
            const from = subYears(to, 3)
            return [from, to]
          },
        },
        {
          label: 'All Time',
          getValue: () => {
            const from = start
            const to = end
            return [from, to]
          },
        },
      ]
    },
})

export const validDateRangeForBrand = selector<MobilityDateRange>({
  key: key('valid-date-range-for-brand'),
  get: async ({ get }) => {
    const brand = get(alphamapBrand)

    if (!brand) {
      return get(validDateRange)
    }

    const { from, to } = await alphamapClient.brandsMobility.getValidVisitsDateRange(brand.id)

    return {
      start: getDateWithoutBloodyTZ(from),
      end: getDateWithoutBloodyTZ(to),
    }
  },
})

export const validEndDateForBrand = selector<Date>({
  key: key('valid-end-date-for-brand'),
  get: async ({ get }) => {
    const { end } = get(validDateRangeForBrand)
    return end
  },
})
